import { displayColonyUsername, sanitizeColonyUsername } from '@/lib/colonyUsername'
import { lumi } from '@/lib/lumi'
import { isHttpImageUrl } from '@/lib/tokenImage'

export type WalletProfile = {
  wallet: string
  username?: string
  image: string
  banner?: string
}

export type WalletProfileSummary = {
  wallet: string
  username: string
  image: string
  banner: string
  updatedAt?: string
  createdAt?: string
}

export type WalletProfileMap = Record<string, { image: string, username: string }>

const AUTH_PREFIX = 'Penguin Pad: authorize profile photo'
const AUTH_TTL_MS = 30 * 60 * 1000
const PROFILE_UPDATED_EVENT = 'penguin-pad-wallet-profile-updated'
const imageCache = new Map<string, string>()
const bannerCache = new Map<string, string>()
const usernameCache = new Map<string, string>()

function normalizeWallet(value?: string) {
  return value?.trim().toLowerCase() || ''
}

function buildAuthMessage(wallet: string) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${AUTH_PREFIX}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function invokeErrorMessage(error: unknown, fallback: string) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || fallback).slice(0, 180)
}

function remember(wallet: string, image?: string, banner?: string, username?: string) {
  const key = normalizeWallet(wallet)
  if (!key)
    return
  if (image && isHttpImageUrl(image))
    imageCache.set(key, image)
  if (banner && isHttpImageUrl(banner))
    bannerCache.set(key, banner)
  if (typeof username === 'string')
    usernameCache.set(key, username.trim())
}

function parseProfileRow(item: unknown): WalletProfileSummary | null {
  if (!item || typeof item !== 'object')
    return null
  const row = item as Record<string, unknown>
  const wallet = typeof row.wallet === 'string' ? row.wallet : ''
  if (!wallet)
    return null
  const username = typeof row.username === 'string' ? row.username.trim() : ''
  const image = typeof row.image === 'string' ? row.image : ''
  const banner = typeof row.banner === 'string' ? row.banner : ''
  const updatedAt = typeof row.updatedAt === 'string' ? row.updatedAt : undefined
  const createdAt = typeof row.createdAt === 'string' ? row.createdAt : undefined
  remember(wallet, image, banner, username)
  return { wallet, username, image, banner, updatedAt, createdAt }
}

export function getCachedWalletImage(wallet?: string) {
  const key = normalizeWallet(wallet)
  return key ? imageCache.get(key) : undefined
}

export function getCachedWalletBanner(wallet?: string) {
  const key = normalizeWallet(wallet)
  return key ? bannerCache.get(key) : undefined
}

export function getCachedWalletUsername(wallet?: string) {
  const key = normalizeWallet(wallet)
  return key ? usernameCache.get(key) : undefined
}

export function colonyDisplayName(wallet?: string, username?: string) {
  return displayColonyUsername(username || getCachedWalletUsername(wallet))
}

export async function listWalletProfiles(wallets: string[]): Promise<WalletProfileMap> {
  const unique = [...new Set(wallets.map(normalizeWallet).filter(Boolean))]
  if (!unique.length)
    return {}

  const missing = unique.filter(wallet => !imageCache.has(wallet) && !bannerCache.has(wallet) && !usernameCache.has(wallet))
  if (missing.length) {
    try {
      const result = await lumi.functions.invoke('walletProfiles', {
        method: 'POST',
        body: { action: 'list', wallets: missing },
      }) as { ok?: boolean, profiles?: unknown } | undefined
      if (result?.ok && Array.isArray(result.profiles)) {
        for (const item of result.profiles)
          parseProfileRow(item)
      }
    }
    catch (error) {
      console.error('List wallet profiles failed:', error)
    }
  }

  const out: WalletProfileMap = {}
  for (const wallet of unique) {
    out[wallet] = {
      image: imageCache.get(wallet) || '',
      username: usernameCache.get(wallet) || '',
    }
  }
  return out
}

export async function fetchWalletProfile(wallet: string): Promise<WalletProfileSummary> {
  const key = normalizeWallet(wallet)
  if (!key)
    return { wallet: key, username: '', image: '', banner: '' }
  try {
    const result = await lumi.functions.invoke('walletProfiles', {
      method: 'POST',
      body: { action: 'list', wallets: [key] },
    }) as { ok?: boolean, profiles?: unknown } | undefined
    const parsed = parseProfileRow(Array.isArray(result?.profiles) ? result.profiles[0] : undefined)
    return parsed || { wallet: key, username: usernameCache.get(key) || '', image: imageCache.get(key) || '', banner: bannerCache.get(key) || '' }
  }
  catch (error) {
    console.error('Fetch wallet profile failed:', error)
    return { wallet: key, username: usernameCache.get(key) || '', image: imageCache.get(key) || '', banner: bannerCache.get(key) || '' }
  }
}

export async function browseWalletProfiles(input: { limit?: number, skip?: number, sort?: 'newest' | 'oldest' } = {}): Promise<WalletProfileSummary[]> {
  try {
    const result = await lumi.functions.invoke('walletProfiles', {
      method: 'POST',
      body: { action: 'browse', limit: input.limit ?? 60, skip: input.skip ?? 0, sort: input.sort ?? 'newest' },
    }) as { ok?: boolean, profiles?: unknown } | undefined
    if (!result?.ok || !Array.isArray(result.profiles))
      return []
    return result.profiles.map(parseProfileRow).filter((row): row is WalletProfileSummary => Boolean(row))
  }
  catch (error) {
    console.error('Browse wallet profiles failed:', error)
    return []
  }
}

export async function saveWalletProfile(input: {
  wallet: string
  image?: string
  banner?: string
  username?: string
  signMessage: (message: string) => Promise<string>
}): Promise<WalletProfile> {
  const wallet = normalizeWallet(input.wallet)
  const image = input.image?.trim() || ''
  const banner = input.banner?.trim() || ''
  const hasUsername = Object.prototype.hasOwnProperty.call(input, 'username')
  const username = hasUsername ? sanitizeColonyUsername(input.username) : undefined
  if (!wallet)
    throw new Error('Connect your wallet to save a profile')
  if (image && !isHttpImageUrl(image))
    throw new Error('A valid photo URL is required')
  if (banner && !isHttpImageUrl(banner))
    throw new Error('A valid banner URL is required')
  if (!image && !banner && !hasUsername)
    throw new Error('Choose a username, photo, or banner to save')

  const message = buildAuthMessage(wallet)
  const signature = (await input.signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  try {
    const result = await lumi.functions.invoke('walletProfiles', {
      method: 'POST',
      body: {
        action: 'save',
        wallet,
        message,
        signature,
        image: image || undefined,
        banner: banner || undefined,
        username: hasUsername ? username : undefined,
      },
    }) as { ok?: boolean, error?: string, profile?: WalletProfile } | undefined
    if (!result || result.ok === false || !result.profile?.wallet)
      throw new Error(result?.error || 'Could not save this profile')
    remember(result.profile.wallet, result.profile.image, result.profile.banner, result.profile.username)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT, { detail: result.profile }))
    }
    return result.profile
  }
  catch (error) {
    console.error('Save wallet profile failed:', error)
    throw new Error(invokeErrorMessage(error, 'Could not save this profile'))
  }
}

export function onWalletProfileUpdated(listener: (profile: WalletProfile) => void) {
  if (typeof window === 'undefined')
    return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<WalletProfile>).detail
    if (!detail?.wallet)
      return
    remember(detail.wallet, detail.image, detail.banner, detail.username)
    listener(detail)
  }
  window.addEventListener(PROFILE_UPDATED_EVENT, handler)
  return () => window.removeEventListener(PROFILE_UPDATED_EVENT, handler)
}
